import React, { useState, useEffect, useCallback } from 'react';
import { ArrowPathIcon, BuildingStorefrontIcon, ExclamationTriangleIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { fetchVerifiedSuppliers, fetchB2bOrders } from '../api/marketplaceApi';

const BuyerSuppliers: React.FC = () => {
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [orderCounts, setOrderCounts] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [supplierRes, ordersRes] = await Promise.all([
        fetchVerifiedSuppliers(),
        fetchB2bOrders().catch(() => ({ items: [] })),
      ]);

      const supplierList = supplierRes.suppliers || supplierRes.items || supplierRes.data || [];
      setSuppliers(supplierList);

      // Count orders per supplier
      const orders = ordersRes.items || ordersRes.data || [];
      const counts: Record<number, number> = {};
      for (const order of orders) {
        const tenantId = order.supplier_tenant_id || order.tenant_id;
        if (tenantId) {
          counts[tenantId] = (counts[tenantId] || 0) + 1;
        }
      }
      setOrderCounts(counts);
    } catch (err: any) {
      setError(err.message || 'Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="relative">
          <div className="w-10 h-10 border-4 border-emerald-200 rounded-full"></div>
          <div className="w-10 h-10 border-4 border-emerald-600 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <ExclamationTriangleIcon className="w-8 h-8 text-red-400 mx-auto" />
        <p className="text-red-700 font-medium mt-2">{error}</p>
        <button onClick={fetchData} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">Retry</button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Verified Suppliers</h2>
        <button onClick={fetchData} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowPathIcon className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {suppliers.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-xl p-12 text-center">
          <BuildingStorefrontIcon className="w-12 h-12 text-gray-300 mx-auto" />
          <p className="text-gray-500 mt-3 font-medium">No verified suppliers yet</p>
          <p className="text-gray-400 text-sm mt-1">Verified suppliers will appear here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {suppliers.map((supplier: any) => (
            <div key={supplier.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center shadow-md shrink-0">
                  {supplier.logo_url ? (
                    <img src={supplier.logo_url} alt="" className="w-full h-full object-cover rounded-xl" />
                  ) : (
                    <BuildingStorefrontIcon className="w-6 h-6 text-white" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <h3 className="font-bold text-gray-900 text-sm truncate">{supplier.business_name}</h3>
                    {supplier.verification_status === 'verified' && (
                      <ShieldCheckIcon className="w-4 h-4 text-blue-500 shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{supplier.business_type || 'Agricultural Supplier'}</p>
                  {supplier.city && <p className="text-xs text-gray-400 mt-0.5">{supplier.city}{supplier.country ? `, ${supplier.country}` : ''}</p>}
                </div>
              </div>
              {supplier.description && (
                <p className="text-xs text-gray-500 mt-3 line-clamp-2">{supplier.description}</p>
              )}
              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs text-gray-400">
                  {orderCounts[supplier.tenant_id] || 0} order{(orderCounts[supplier.tenant_id] || 0) !== 1 ? 's' : ''}
                </span>
                <span className="text-xs font-medium text-emerald-600">Verified</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BuyerSuppliers;
